import React from 'react';
import {
  PieChart, Pie, Legend, Tooltip, ResponsiveContainer, Cell,
  LabelList,
} from 'recharts';
import isMobile from '../utils/isMobile';
import { designTokens } from '../design-system/tokens';

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    dataKey: string;
    percent?: number;
  }>;
}

interface PieChartProps {
  data: { name: string; value: number }[];
  currency?: boolean;
}

const CustomPieChart = ({ data }: PieChartProps) => {
  // Use design tokens for chart colors - create a gradient from primary to secondary
  const CHART_COLORS = [
    designTokens.colors.brand.primary[200],
    designTokens.colors.brand.primary[400],
    designTokens.colors.brand.primary[600],
    designTokens.colors.brand.secondary[200],
    designTokens.colors.brand.secondary[400],
    designTokens.colors.brand.secondary[600],
  ];

  const THRESHOLD_PERCENTAGE = 0.02;

  let otherValue = 0;
  const otherData = [];
  const mainData = [];

  for (const item of data) {
    if (item.value < THRESHOLD_PERCENTAGE) {
      otherValue += item.value;
    } else {
      mainData.push(item);
    }
  }

  if (otherValue > 0) {
    otherData.push({ name: 'Otros', value: otherValue });
  }

  const processedData = [...mainData, ...otherData];

  const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip bg-surface-base p-2 border border-border-primary rounded shadow-md">
          <p className="label font-medium text-text-primary">{`${payload[0].name}: ${(payload[0].value * 100).toFixed(1)}% del total`}</p>
        </div>
      );
    }
    return null;
  };

  const hasValidData = processedData.length > 0 && processedData.some((item) => item.value !== null && item.value !== undefined);

  return (
    <ResponsiveContainer width="100%" height={300}>
      {hasValidData ? <PieChart className='mx-auto' style={{ userSelect: 'none' }}>
        <Tooltip content={<CustomTooltip />} />
        <Pie
          dataKey="value"
          data={processedData}
          cx="50%"
          cy="50%"
          outerRadius={80}
          fill={designTokens.colors.brand.primary[500]}
          innerRadius={40}
          fontSize={14}
          style={{ outline: 'none' }}
          paddingAngle={0}
          stroke="none"
        >
          {processedData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={CHART_COLORS[index % CHART_COLORS.length]}
              stroke="none"
            />
          ))}
          {!isMobile() && (
            <LabelList
              dataKey="name"
              position="outside"
              fill={designTokens.colors.neutral[900]}
              stroke="none"
              offset={15}
            />
          )}
        </Pie>
        <Legend
          layout="horizontal"
          verticalAlign="bottom"
          align="center"
          payload={
            processedData.map((item, index) => ({
              id: item.name,
              type: 'circle',
              value: `${isMobile() ? (`${item.name} ${(item.value * 100).toFixed(1)}%`) : item.name}`,
              color: CHART_COLORS[index % CHART_COLORS.length],
            }))
          }
          formatter={(value) => (
            <span className='font-medium'>
              {value}
            </span>
          )}
        />
      </PieChart>
        : <div className='flex flex-col items-center justify-center h-full'>
          <p className='text-text-secondary'>No hay datos</p>
        </div>}
    </ResponsiveContainer>
  );
};

export default CustomPieChart;
